import React, { useMemo, useState, memo } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { makeStyles } from "@mui/styles";

// Core Components
import MDCTooltip from "./MDCTooltip";

import SearchIcon from "../iconComponents/SearchIcon";

const useStyles = makeStyles((theme) => ({
  defaultDropDownCSS: {
    "& .MuiListSubheader-root": {
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      color: "#000 !important",
      fontFamily: theme.palette.typography.fontFamily,
    },
    "& .MuiAutocomplete-input": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      paddingRight: "55px !important",
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.MDCColors.color23,
    },
    marginTop: "1rem",
    "& .MuiOutlinedInput-root": {
      padding: "4px !important",
      color: theme.palette.MDCColors.color18,
      fontWeight: "initial !important",
    },
    "& .MuiAutocomplete-root": {
      border: `2px solid ${theme.palette.MDCColors.color23}`,
      borderRadius: "5px",
      background: theme.palette.MDCColors.color21,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  noRotateIcon: {
    "& .MuiAutocomplete-popupIndicator": {
      transform: "none !important",
    },
  },
  errorText: {
    "& .MuiAutocomplete-root": {
      border: `2px solid ${theme.palette.MDCColors.errorDanger}`,
      background: "rgba(236, 88, 103, 0.3)",
    },
  },
  disabledMenu: {
    "& .MuiAutocomplete-root": {
      border: "0px",
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.MDCColors.color18,
      opacity: 0.5,
    },
    "& .MuiAutocomplete-popupIndicator svg": {
      fill: `${theme.palette.MDCColors.color18} !important`,
      opacity: 0.5,
    },
    "& .MuiAutocomplete-popupIndicator svg path": {
      fill: `${theme.palette.MDCColors.color18} !important`,
    },
  },
  subheaderCSS: {
    fontFamily: theme.palette.typography.fontFamily,
    color: "#5F5F5F !important",
  },
  headerStyle: {
    padding: "6px 10px",
    fontFamily: theme.palette.typography.fontFamily,
  },
}));

const MDCGroupedIconDropdown = memo((props) => {
  const {
    options,
    value,
    onChange,
    onOptionChange,
    error,
    disabled,
    placeholder,
    showIcon,
  } = props;

  const {
    defaultDropDownCSS,
    errorText,
    disabledMenu,
    subheaderCSS,
    noRotateIcon,
    headerStyle,
  } = useStyles();

  const [searchValue, setSearchValue] = useState("");

  let MDCStyles = defaultDropDownCSS;
  if (error) {
    MDCStyles = `${MDCStyles} ${errorText}`;
  }
  if (disabled) {
    MDCStyles = `${MDCStyles} ${disabledMenu}`;
  }
  if (showIcon) {
    MDCStyles = `${MDCStyles} ${noRotateIcon}`;
  }

  // Options are expected to be pre-flattened by the caller.
  // Shape: [{ header, subHeader, groupOrder? }]
  const flatOptions = options || [];

  const displayOptions = useMemo(
    () =>
      flatOptions.filter(
        (item, index, arr) =>
          arr.findIndex(
            (x) => x.header === item.header && x.subHeader === item.subHeader
          ) === index
      ),
    [flatOptions]
  );

  // Memoize selected value to maintain stable reference
  const selectedValue = useMemo(() => {
    if (value && flatOptions?.length) {
      const found = flatOptions.find((item) => item?.subHeader === value);
      if (found) return found;
    }
    // Return value directly if no match (handles loading state)
    return value || null;
  }, [value, flatOptions]);

  const onValueChange = (newValue) => {
    // Send sub-header to backend for Rechtsform
    onChange(newValue === null ? "" : newValue?.subHeader);

    // Optional callback for UI state (e.g., showing selected header)
    if (typeof onOptionChange === "function") {
      onOptionChange(newValue || null);
    }
  };

  return (
    <div className={MDCStyles}>
      <Autocomplete
        popupIcon={showIcon && <SearchIcon />}
        value={selectedValue}
        disablePortal
        options={displayOptions}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") {
            setSearchValue(newInputValue);
          }
          if (reason === "reset") {
            return;
          }
          if (reason === "clear") {
            setSearchValue("");
          }
          if (reason === "blur" && !newInputValue) {
            setSearchValue("");
          }
        }}
        groupBy={(option) => option?.header}
        renderGroup={(params) => (
          <li key={params.key}>
            <div className={headerStyle}>{params.group}</div>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option?.subHeader || ""
        }
        getOptionKey={(option) =>
          typeof option === "string"
            ? option
            : `${option?.header || ""}-${option?.subHeader || ""}`
        }
        isOptionEqualToValue={(option, value) =>
          option?.header === value?.header &&
          option?.subHeader === value?.subHeader
        }
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder} />
        )}
        disabled={disabled}
        onChange={(e, newValue) => {
          onValueChange(newValue);
        }}
        noOptionsText={"Ihre Suche ergab keinen Treffer."}
        renderOption={(props, option) => (
          <li {...props} className={`${props.className} ${subheaderCSS}`}>
            <MDCTooltip>
              <span>{option?.subHeader}</span>
            </MDCTooltip>
          </li>
        )}
      />
    </div>
  );
});

export default MDCGroupedIconDropdown;
